import React from 'react';
import { Box, Paper, Typography, ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { Balance, EventNote } from '@mui/icons-material';
import { TaxFramework, FRAMEWORKS } from '../utils/taxRulesEngine';

interface TaxYearSelectorProps {
  selectedFramework: TaxFramework;
  onChange: (framework: TaxFramework) => void;
  compact?: boolean;
}

export const TaxYearSelector: React.FC<TaxYearSelectorProps> = ({
  selectedFramework,
  onChange,
  compact = false
}) => {
  const currentInfo = FRAMEWORKS[selectedFramework];

  return (
    <Paper
      elevation={24}
      sx={{
        p: compact ? 2 : 2.5,
        borderRadius: 4,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        color: '#123D6B',
        border: '1px solid rgba(255, 255, 255, 0.7)',
        boxShadow: '0px 20px 45px rgba(11, 40, 71, 0.1)',
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 1.2, bgcolor: 'rgba(197, 155, 39, 0.15)', borderRadius: 2 }}>
            <EventNote sx={{ color: '#C59B27', fontSize: '1.8rem' }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.8, textTransform: 'uppercase', mb: 0.2 }}>
              Select Legal Framework / Tax Year
            </Typography>
            <Typography variant="body1" fontWeight={800} sx={{ color: '#0B2847', fontSize: '1.1rem' }}>
              {currentInfo.actName}
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup
          value={selectedFramework}
          exclusive
          onChange={(_, val) => val && onChange(val)}
          size="small"
          sx={{
            bgcolor: '#F1F5F9',
            p: 0.6,
            borderRadius: 3,
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              color: '#475569',
              border: 'none',
              borderRadius: 2.5,
              px: 2.5,
              py: 0.9,
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: 'rgba(11, 40, 71, 0.05)' },
              '&.Mui-selected': {
                bgcolor: '#123D6B',
                color: '#FFFFFF',
                fontWeight: 700,
                boxShadow: '0 4px 10px rgba(11, 40, 71, 0.2)',
                '&:hover': { bgcolor: '#0B2847' }
              }
            }
          }}
        >
          <ToggleButton value="TAX_YEAR_2026_27_2025_ACT">
            Tax Year 2026–27 (2025 Act)
          </ToggleButton>
          <ToggleButton value="AY_2026_27_1961">
            AY 2026–27 (1961 Act)
          </ToggleButton>

        </ToggleButtonGroup>
      </Box>

      {!compact && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<Balance sx={{ fontSize: '1rem !important', color: '#FFD700 !important' }} />}
            label={currentInfo.yearLabel}
            size="small"
            sx={{ bgcolor: 'rgba(197, 155, 39, 0.2)', color: '#FFD700', fontWeight: 700 }}
          />
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            {currentInfo.description}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
